/**
 * Cached access to `site_settings` (header/footer).
 *
 * Motivo: os componentes liam essas configs de forma assíncrona e trocavam o
 * layout depois da hidratação (nav, seções escondidas, CTA), gerando CLS alto.
 * Aqui lemos primeiro de um snapshot injetado no HTML estático
 * (`window.__SITE_SETTINGS__`) ou do cache em localStorage — valores síncronos,
 * sem shift — e só então revalidamos em background.
 */
import { supabase } from "@/integrations/supabase/client";

export type SiteSettingsKey = "header" | "footer";

const CACHE_PREFIX = "site_settings:";

declare global {
  interface Window {
    __SITE_SETTINGS__?: Record<string, unknown>;
  }
}

/** Valor imediato (sem rede). Retorna null quando não há snapshot/cache. */
export function readSettingsSync<T = any>(key: SiteSettingsKey): T | null {
  if (typeof window === "undefined") return null;
  const injected = window.__SITE_SETTINGS__?.[key];
  if (injected && typeof injected === "object") return injected as T;
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

const inflight = new Map<SiteSettingsKey, Promise<any>>();

/** Busca (uma vez por sessão) e atualiza o cache local. */
export function fetchSettings<T = any>(key: SiteSettingsKey): Promise<T | null> {
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = (async () => {
    const { data } = await supabase
      .from("site_settings" as never)
      .select("value")
      .eq("key", key)
      .maybeSingle();
    const value = (data as any)?.value ?? null;
    if (value && typeof window !== "undefined") {
      try {
        localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
      } catch {
        /* storage cheio/bloqueado — ignora */
      }
    }
    return value as T | null;
  })();
  inflight.set(key, p);
  return p;
}
